import axios, { AxiosResponse, AxiosRequestHeaders, AxiosResponseHeaders } from 'axios';
import type { DynamicObjectInterface } from '../type';
import cFetch from './fetchUtils';

interface MockResponse {
  data: DynamicObjectInterface;
  status: number;
  headers: DynamicObjectInterface;
}

type APIResponse = AxiosResponse<DynamicObjectInterface, DynamicObjectInterface> & MockResponse;

export type AxiosUtilsType = Promise<APIResponse>;

const mockResolve = (mockData?: DynamicObjectInterface) => ({
  data: mockData ?? {},
  status: 200,
});

export const axiosGet = async (url: string, mockData?: DynamicObjectInterface, forceMock = false): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.get(url);
  }
};

export const axiosPost = async (
  url: string,
  payload?: DynamicObjectInterface,
  mockData?: DynamicObjectInterface,
  forceMock = false,
): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.post(url, payload ?? {});
  }
};

export const axiosPut = async (
  url: string,
  payload?: DynamicObjectInterface,
  mockData?: DynamicObjectInterface,
  forceMock = false,
): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.put(url, payload ?? {});
  }
};

export const axiosPatch = async (
  url: string,
  payload?: DynamicObjectInterface,
  mockData?: DynamicObjectInterface,
  forceMock = false,
): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.patch(url, payload ?? {});
  }
};

export const axiosDelete = async (
  url: string,
  payload?: DynamicObjectInterface,
  mockData?: DynamicObjectInterface,
  forceMock = false,
): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.delete(url, { data: payload ?? {} });
  }
};

export const axiosPostFile = async (url: string, formData: FormData): AxiosUtilsType => {
  let headers: DynamicObjectInterface = {};
  headers['Content-Type'] = 'multipart/form-data'; // this not working
  return await axios.postForm(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...headers,
    },
  });
};

export const postFile = async (url: string, formData: FormData): AxiosUtilsType => {
  const response = await cFetch(url, {
    method: 'POST',
    body: formData,
  });

  // Transform to axios format for standardization
  const responseData = await response.json();

  const axiosResponse: AxiosResponse = {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
    headers: convertHeaders(response.headers),
    config: {
      headers: convertHeaders(response.headers),
    }, // Customize as needed
  };

  return axiosResponse;
};

// for data migration purpose only
export const postMigrationFile = async (url: string, formData: FormData): AxiosUtilsType => {
  const response = await cFetch(url, {
    method: 'POST',
    body: formData,
  });

  // Transform to axios format for standardization
  const responseData = await response.blob();

  const axiosResponse: AxiosResponse = {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
    headers: convertHeaders(response.headers),
    config: {
      headers: convertHeaders(response.headers),
    }, // Customize as needed
  };

  return axiosResponse;
};

const convertHeaders = (headers: Headers): AxiosResponseHeaders => {
  const convertedHeaders: AxiosResponseHeaders = {} as AxiosRequestHeaders;
  headers.forEach((value, name) => {
    convertedHeaders[name.toLowerCase()] = value;
  });
  return convertedHeaders;
};

// for mdms get new abo
export const axiosGetMdms = async (
  url: string,
  mockData?: DynamicObjectInterface,
  forceMock = false,
): AxiosUtilsType => {
  if (process.env.REACT_APP_OFFLINE === 'true' || forceMock) {
    return Promise.resolve(mockResolve(mockData) as APIResponse);
  } else {
    return await axios.get(url, {
      timeout: 3600000,
    });
  }
};
